import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  CloudUpload, FileArchive, Upload, CheckCircle2, AlertTriangle, Loader2,
  ChevronRight, ImageOff, Cloud, History, Trash2, Activity, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  clydeflowPreview, clydeflowCommit, clydeflowHistory,
  clydeflowDriveStatus, clydeflowDriveScanNow,
  clydeflowDriveConnect, clydeflowDriveDisconnect,
  clydeflowConnectServiceAccount,
  clydeflowRetentionStats, clydeflowRetentionRunNow,
} from '@/lib/api';

export const ClydeFlowImport = () => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [driveStatus, setDriveStatus] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [retention, setRetention] = useState(null);
  const [retentionRunning, setRetentionRunning] = useState(false);

  // Service Account connect state
  const [saJson, setSaJson] = useState('');
  const [saFolderId, setSaFolderId] = useState('');
  const [saConnecting, setSaConnecting] = useState(false);
  const [saExpanded, setSaExpanded] = useState(false);

  const loadHistory = async () => {
    try {
      const res = await clydeflowHistory();
      setHistory(res.data || []);
    } catch { /* noop */ }
  };
  const loadDrive = async () => {
    try {
      const res = await clydeflowDriveStatus();
      setDriveStatus(res.data);
    } catch { /* noop */ }
  };
  const loadRetention = async () => {
    try {
      const res = await clydeflowRetentionStats();
      setRetention(res.data);
    } catch { /* noop */ }
  };
  const runRetentionNow = async () => {
    setRetentionRunning(true);
    try {
      const res = await clydeflowRetentionRunNow();
      const d = res.data || {};
      toast.success(
        `Retention pass complete — deactivated ${d.deactivated_expired || 0}, deleted ${d.deleted_clydeflow_older_than_30d || 0}`
      );
      await loadRetention();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retention pass failed');
    } finally {
      setRetentionRunning(false);
    }
  };
  useEffect(() => { loadHistory(); loadDrive(); loadRetention(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!(lower.endsWith('.zip') || lower.endsWith('.csv'))) {
      toast.error('Please drop a ClydeFlow ZIP or CSV file');
      return;
    }
    setParsing(true);
    setPreview(null);
    setResult(null);
    try {
      const res = await clydeflowPreview(file);
      const data = res.data;
      setPreview(data);
      // Pre-select rows that have a link AND don't look like obvious duplicates client-side
      setSelected(new Set(data.rows.filter(r => r.link).map(r => r.row_index)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse bundle');
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const toggleRow = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    if (selected.size === preview.rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(preview.rows.map(r => r.row_index)));
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    if (selected.size === 0) {
      toast.error('Select at least one row to import');
      return;
    }
    setCommitting(true);
    try {
      const res = await clydeflowCommit(preview.session_id, Array.from(selected));
      setResult(res.data);
      toast.success(res.data.message || 'Import complete');
      setPreview(null);
      setSelected(new Set());
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const res = await clydeflowDriveScanNow();
      const r = res.data;
      if (r.status === 'drive_not_connected') {
        toast.error('Google Drive isn\u2019t connected yet — click Connect Google Drive below');
      } else {
        toast.success(`Scan complete — ${r.files_seen || 0} files seen, ${r.imported?.length || 0} imported`);
      }
      loadHistory();
      loadDrive();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await clydeflowDriveConnect();
      // Open Google consent in a new tab
      window.open(res.data.auth_url, '_blank', 'noopener,noreferrer');
      toast.info('Authorise in the new tab — this page will refresh status automatically');
      // Poll status every 3s for up to 2 minutes
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        const s = await clydeflowDriveStatus();
        if (s.data?.connected) {
          clearInterval(poll);
          setDriveStatus(s.data);
          toast.success('Google Drive connected!');
        } else if (tries > 40) {
          clearInterval(poll);
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start OAuth flow');
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect Google Drive? Auto-scan will stop until you reconnect.')) return;
    try {
      await clydeflowDriveDisconnect();
      toast.success('Disconnected');
      loadDrive();
    } catch {
      toast.error('Disconnect failed');
    }
  };

  const handleServiceAccountConnect = async () => {
    if (!saJson.trim()) {
      toast.error('Paste your service account JSON key first');
      return;
    }
    setSaConnecting(true);
    try {
      const res = await clydeflowConnectServiceAccount(saJson.trim(), saFolderId.trim() || null);
      toast.success(res.data.message || 'Service account connected');
      setSaJson('');
      setSaFolderId('');
      setSaExpanded(false);
      loadDrive();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not connect service account');
    } finally {
      setSaConnecting(false);
    }
  };

  const allSelected = preview && selected.size === preview.rows.length;

  return (
    <div data-testid="clydeflow-import-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">ClydeFlow Import</h1>
          <p className="text-slate-600">Upload export bundles from ClydeFlow Local Signals — each row becomes a draft Job.</p>
        </div>
      </div>

      {/* Drop zone */}
      {!preview && !result && (
        <Card
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed transition-colors ${
            dragOver ? 'border-sky-500 bg-sky-50' : 'border-slate-300 bg-slate-50'
          }`}
          data-testid="clydeflow-dropzone"
        >
          <CardContent className="p-12 text-center">
            <CloudUpload className="h-12 w-12 mx-auto mb-4 text-sky-500" />
            <h3 className="font-heading text-xl text-slate-900 mb-2">Drop a ClydeFlow bundle here</h3>
            <p className="text-sm text-slate-500 mb-6">
              Accepts <code className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">clydeflow-jobs-…-with-images.zip</code> or a plain CSV with the standard 10 columns.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".zip,.csv"
              className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              data-testid="clydeflow-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={parsing}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="clydeflow-browse-btn"
            >
              {parsing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Parsing…</>) : (
                <><Upload className="h-4 w-4 mr-2" /> Choose file</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview table */}
      {preview && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileArchive className="h-5 w-5 text-sky-500" /> Preview
                </CardTitle>
                <CardDescription>
                  {preview.row_count} rows
                  {preview.region ? ` · region: ${preview.region}` : ''}
                  {preview.date ? ` · ${preview.date}` : ''}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => { setPreview(null); setSelected(new Set()); }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCommit}
                  disabled={committing || selected.size === 0}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="clydeflow-commit-btn"
                >
                  {committing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>) : (
                    <><CheckCircle2 className="h-4 w-4 mr-2" /> Import {selected.size} draft{selected.size === 1 ? '' : 's'}</>
                  )}
                </Button>
              </div>
            </div>
            {preview.warnings?.length > 0 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <ul className="space-y-1">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      data-testid="clydeflow-select-all"
                    />
                  </TableHead>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Employer</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Salary</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map(r => (
                  <TableRow
                    key={r.row_index}
                    className={!r.link ? 'opacity-40' : ''}
                    data-testid={`clydeflow-row-${r.row_index}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(r.row_index)}
                        onCheckedChange={() => toggleRow(r.row_index)}
                        disabled={!r.link}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{r.row_index}</TableCell>
                    <TableCell>
                      {r.image_thumb_b64 ? (
                        <img src={r.image_thumb_b64} alt="" className="h-10 w-10 rounded object-cover" />
                      ) : r.image_url ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500" title={r.image_url}>
                          <Cloud className="h-3 w-3" /> URL
                        </span>
                      ) : (
                        <ImageOff className="h-4 w-4 text-slate-300" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900 line-clamp-1 max-w-xs">{r.title}</div>
                      {!r.link && <Badge variant="outline" className="mt-0.5 text-[10px]">no link</Badge>}
                    </TableCell>
                    <TableCell className="text-sm">{r.employer}</TableCell>
                    <TableCell className="text-sm">{r.location}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.salary}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Result summary */}
      {result && (
        <Card className="mb-6 border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> Import complete
            </CardTitle>
            <CardDescription>{result.message}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Created" value={result.created} colour="text-emerald-600" />
            <Stat label="Duplicates skipped" value={result.skipped_duplicate} colour="text-slate-500" />
            <Stat label="Missing link" value={result.skipped_no_link} colour="text-slate-500" />
            <Stat label="Failed" value={result.failed} colour={result.failed ? 'text-red-600' : 'text-slate-400'} />
            {result.failures?.length > 0 && (
              <div className="col-span-full mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3 space-y-1">
                {result.failures.map((f, i) => <div key={i}>Row {f.row}: {f.reason}</div>)}
              </div>
            )}
            <div className="col-span-full">
              <Button variant="outline" onClick={() => setResult(null)}>Import another bundle</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drive status + scan */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-sky-500" /> Google Drive (optional)
          </CardTitle>
          <CardDescription>
            <strong>Recommended workflow:</strong> drop ZIPs directly into the upload area above — it&apos;s instant and works without Google verification.
            <br />
            Connecting Drive lets the daily 12&nbsp;pm scan import files you&apos;ve explicitly &quot;Opened with&quot; this app. Other files in your Drive remain private &amp; invisible to us (<code className="text-xs bg-slate-100 px-1 rounded">drive.file</code> scope — no padlock screen).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {driveStatus?.connected ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Connected ({driveStatus.auth_type === 'service_account' ? 'service account' : 'OAuth'}) — next run: 12:00 daily
                </div>
                {driveStatus.service_account_email && (
                  <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600 max-w-full overflow-x-auto">
                    {driveStatus.service_account_email}
                  </code>
                )}
                {driveStatus.folder_id && (
                  <span className="text-xs text-slate-500">Folder ID: <code className="bg-slate-100 px-1 rounded">{driveStatus.folder_id}</code></span>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleScanNow} disabled={scanning} variant="outline" data-testid="clydeflow-scan-now">
                  {scanning ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</>) : (
                    <>Run scan now <ChevronRight className="h-4 w-4 ml-1" /></>
                  )}
                </Button>
                <Button onClick={handleDisconnect} variant="ghost" className="text-red-600 hover:text-red-700" data-testid="clydeflow-disconnect">
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-600 space-y-4">
              {/* Service Account — recommended */}
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-emerald-900 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" /> Connect with Service Account
                      <Badge variant="outline" className="ml-1 text-[10px] border-emerald-400 text-emerald-700">Recommended</Badge>
                    </div>
                    <p className="text-xs text-emerald-800/80 mt-1">
                      No padlock screen, no expiry. Authenticates as a robot identity — you control access by sharing your ClydeFlow folder with it.
                    </p>
                  </div>
                  {!saExpanded && (
                    <Button onClick={() => setSaExpanded(true)} size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                      Set up
                    </Button>
                  )}
                </div>
                {saExpanded && (
                  <div className="mt-4 space-y-3" data-testid="service-account-form">
                    <ol className="list-decimal list-inside space-y-1 text-xs text-slate-600 pl-2">
                      <li>Google Cloud Console → IAM &amp; Admin → <strong>Service Accounts</strong> → <strong>Create</strong> (any name; skip optional steps)</li>
                      <li>Open the new account → <strong>Keys</strong> → <strong>Add key</strong> → <strong>Create new key</strong> → JSON (downloads a file)</li>
                      <li>In Google Drive, share your ClydeFlow folder (or root files) with the service account&apos;s <code className="bg-slate-100 px-1 rounded">…@….iam.gserviceaccount.com</code> email as <strong>Viewer</strong></li>
                      <li>Paste the full JSON below and click <strong>Connect service account</strong></li>
                    </ol>
                    <div>
                      <label className="text-xs font-medium text-slate-700 block mb-1">Service Account JSON key</label>
                      <textarea
                        value={saJson}
                        onChange={(e) => setSaJson(e.target.value)}
                        rows={6}
                        placeholder='{ "type": "service_account", "project_id": "...", "private_key": "...", "client_email": "...", ... }'
                        className="w-full font-mono text-xs border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        data-testid="sa-json-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700 block mb-1">
                        Folder ID <span className="font-normal text-slate-500">(optional — leave blank to scan root)</span>
                      </label>
                      <input
                        value={saFolderId}
                        onChange={(e) => setSaFolderId(e.target.value)}
                        placeholder="1a2BcDEfGhIjKlMnoPqRsTuVw…"
                        className="w-full font-mono text-xs border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        data-testid="sa-folder-input"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">From the Drive folder URL: /drive/folders/<strong>this-bit</strong></p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleServiceAccountConnect}
                        disabled={saConnecting || !saJson.trim()}
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid="sa-connect-btn"
                      >
                        {saConnecting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating…</>) : (
                          <><CheckCircle2 className="h-4 w-4 mr-2" /> Connect service account</>
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setSaExpanded(false); setSaJson(''); setSaFolderId(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* OAuth — fallback */}
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer hover:text-slate-700">
                  Or connect with personal Google account (OAuth)
                </summary>
                <div className="mt-2 pl-4 border-l-2 border-slate-200 space-y-2">
                  <p>Uses your own Google account. Comes with a one-time &quot;app not verified&quot; screen since this app isn&apos;t in Google&apos;s approved list yet.</p>
                  {driveStatus?.client_id_configured === false ? (
                    <span className="text-amber-700">Server missing GOOGLE_CLIENT_ID/SECRET.</span>
                  ) : (
                    <Button
                      onClick={handleConnect}
                      size="sm"
                      variant="outline"
                      data-testid="clydeflow-connect"
                    >
                      <Cloud className="h-3.5 w-3.5 mr-1.5" /> Connect via OAuth
                    </Button>
                  )}
                </div>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Retention & Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-emerald-600" />
            Retention &amp; health
          </CardTitle>
          <CardDescription>
            Jobs auto-expire after <strong>10 days</strong> (marked inactive) and are hard-deleted <strong>30 days</strong> after import.
            Duplicates are blocked on the way in by matching the source link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {retention ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-slate-200 rounded-lg p-3" data-testid="retention-stat-total">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Total imported</div>
                  <div className="text-2xl font-bold text-slate-900">{retention.total_clydeflow_jobs ?? 0}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3" data-testid="retention-stat-live">
                  <div className="text-xs text-emerald-700 uppercase tracking-wide">Live now</div>
                  <div className="text-2xl font-bold text-emerald-700">{retention.live ?? 0}</div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3" data-testid="retention-stat-expired">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Expired (inactive)</div>
                  <div className="text-2xl font-bold text-slate-500">{retention.expired_inactive ?? 0}</div>
                </div>
                <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3" data-testid="retention-stat-delete-soon">
                  <div className="text-xs text-amber-700 uppercase tracking-wide">Delete within 3d</div>
                  <div className="text-2xl font-bold text-amber-700">{retention.delete_within_3_days ?? 0}</div>
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {retention.last_retention_run ? (
                    <>
                      Last cleanup:{' '}
                      <span className="text-slate-700 font-medium">
                        {new Date(retention.last_retention_run.ran_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                      {' '}(deactivated {retention.last_retention_run.deactivated_expired}, deleted {retention.last_retention_run.deleted_clydeflow_older_than_30d})
                    </>
                  ) : (
                    <>Runs automatically at 12:00 daily. No manual cleanup yet.</>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runRetentionNow}
                  disabled={retentionRunning}
                  data-testid="retention-run-now"
                >
                  {retentionRunning
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Cleaning…</>
                    : <><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Run cleanup now</>
                  }
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-2">Loading retention stats…</div>
          )}
        </CardContent>
      </Card>

      {/* Run history */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4 text-slate-500" /> Recent runs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Files</TableHead>
                  <TableHead>Imported</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h, i) => {
                  const created = (h.imported || []).reduce((acc, x) => acc + (x.created || 0), 0);
                  const warnings = h.warnings || [];
                  const rawFiles = h.raw_files_in_folder || [];
                  const errors = h.errors || [];
                  const hasIssue = warnings.length > 0 || errors.length > 0;
                  return (
                    <TableRow key={i} data-testid={`clydeflow-run-${i}`}>
                      <TableCell className="text-xs align-top">{new Date(h.ran_at).toLocaleString('en-GB')}</TableCell>
                      <TableCell className="text-xs align-top">{h.trigger || 'manual'}</TableCell>
                      <TableCell className="text-xs align-top">
                        {h.files_seen ?? '—'}
                        {h.folder_id_scanned && (
                          <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                            folder: {h.folder_id_scanned === 'root' ? 'root ⚠' : h.folder_id_scanned}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs align-top">{created} created</TableCell>
                      <TableCell className="text-xs align-top">
                        <Badge variant={h.status === 'ok' && !hasIssue ? 'default' : 'secondary'}>
                          {h.status || 'unknown'}{hasIssue ? ' ⚠' : ''}
                        </Badge>
                        {(warnings.length > 0 || rawFiles.length > 0 || errors.length > 0) && (
                          <details className="mt-1.5 max-w-md">
                            <summary className="text-[11px] cursor-pointer text-amber-700 hover:text-amber-800">
                              Diagnostics
                            </summary>
                            <div className="mt-1 space-y-1 text-[11px]">
                              {errors.map((e, ei) => (
                                <div key={`err-${ei}`} className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                                  ✕ {typeof e === 'string' ? e : (e.reason || JSON.stringify(e))}
                                </div>
                              ))}
                              {warnings.map((w, wi) => (
                                <div key={`warn-${wi}`} className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                  ⚠ {w}
                                </div>
                              ))}
                              {rawFiles.length > 0 && (
                                <div className="text-slate-600">
                                  <div className="font-medium mb-0.5">Files in folder ({rawFiles.length}):</div>
                                  <ul className="pl-3 space-y-0.5 max-h-40 overflow-y-auto">
                                    {rawFiles.map((f, fi) => (
                                      <li key={fi} className="font-mono">
                                        {f.name} <span className="text-slate-400">({f.mimeType})</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </details>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Stat = ({ label, value, colour }) => (
  <div className="text-center sm:text-left">
    <div className={`text-2xl font-bold ${colour}`}>{value}</div>
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
  </div>
);

export default ClydeFlowImport;
